import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react'
import type { LayoutChangeEvent, ViewStyle } from 'react-native'
import { DNABox } from '@alucio/lux-ui'

const CenteredGridLayout: React.FC<React.PropsWithChildren<{ itemWidth: number, itemCount: number }>> = (props) => {
  const { itemWidth, itemCount = 0, children } = props
  
  const [centeringMargin, setCenteringMargin] = useState<number>(-1)
  
  // [NOTE] - State management to help reduce layout shift since onLayout would be called / calculated one frame after the actual margins should be updated
  //        - We hide the component until the new margins have been fully resolved reducing visual jank
  const [didRender, setDidRender] = useState<boolean>(false)
  const prevItemCount = useRef<number>(itemCount)
  const isUpdating = (!didRender) || (prevItemCount.current !== itemCount) || (centeringMargin === -1)

  useEffect(
    () => { setDidRender(false) },
    [itemCount]
  )

  useEffect(
    () => {
      if (!didRender) {
        // Use setTimeout to force tick the next frame
        const timer = setTimeout(
          () => {
            setDidRender(true)
            prevItemCount.current = itemCount
          },
          0
        )
        return () => clearTimeout(timer)
      }
    },
    [didRender, prevItemCount, itemCount]
  )

  const handleContainerLayout = useCallback(
    (e: LayoutChangeEvent) => {
      const containerWidth = (e.nativeEvent.layout.width)
      
      // If no items, don't set a margin
      if (itemCount === 0) {
        setCenteringMargin(0)
        return;
      }

      const maxItemsPerRow = Math.floor((containerWidth / itemWidth))
      // Centering math is based on how many items should render, if we can fill at least on or more rows, use max amount of items space allows for
      // Otherwise use the actual item count
      const targetRowItemCount = itemCount >= maxItemsPerRow
        ? maxItemsPerRow
        : itemCount

      const leftMargin = (containerWidth - (targetRowItemCount * itemWidth)) / 2

      setCenteringMargin(leftMargin)
    },
    [setCenteringMargin, itemCount]
  )
  
  const centeringGridStyle = useMemo<ViewStyle>(
    () => ({
      // [NOTE] - This hides the visual layoutshift on first render
      ...(isUpdating ? { opacity: 0 } : { }),
      marginLeft: centeringMargin
    }),
    [centeringMargin, didRender, isUpdating]
  )

  return (
    <DNABox fill onLayout={handleContainerLayout}>
      <DNABox fill style={centeringGridStyle}>
        { children }
      </DNABox>
    </DNABox>
  )
}

export default CenteredGridLayout
